/**
 * Settings route per spec section 15.11 and design README section 5.11.
 *
 * Two-column layout: left nav (8 sections grouped into Workspace + Data),
 * right body shows the selected section. All edits auto-save on blur via
 * the `useUpdateSettings` mutation; the `AUTO-SAVED ON BLUR` mono label
 * sits in the header where a Save button would normally be.
 *
 * Sections that lean entirely on Clerk's hosted UI (Profile / passkeys)
 * keep a placeholder until Clerk's `<UserProfile />` component is wired
 * — that lands in M13 polish.
 */

import { useState, type ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { createFileRoute } from '@tanstack/react-router';
import { CheckIcon, ArrowDownTrayIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';

import { PageHeader } from '@/components/shared/PageHeader';
import {
  useSettings,
  useUpdateSettings,
  useImportMondayLeads,
  useImportMondayOpportunities,
  downloadExport,
} from '@/lib/api/settings';
import { cn } from '@/lib/utils';
import type { SettingsState } from '@workspace/shared';

type Section =
  | 'profile'
  | 'tasks'
  | 'sales'
  | 'tags'
  | 'claude'
  | 'integrations'
  | 'import'
  | 'export';

const NAV: { id: Section; label: string; group: 'workspace' | 'data' }[] = [
  { id: 'profile', label: 'Profile', group: 'workspace' },
  { id: 'tasks', label: 'Tasks', group: 'workspace' },
  { id: 'sales', label: 'Sales', group: 'workspace' },
  { id: 'tags', label: 'Tags & templates', group: 'workspace' },
  { id: 'claude', label: 'Claude', group: 'workspace' },
  { id: 'integrations', label: 'Integrations', group: 'data' },
  { id: 'import', label: 'Import', group: 'data' },
  { id: 'export', label: 'Export', group: 'data' },
];

export const Route = createFileRoute('/settings')({
  component: SettingsView,
});

function SettingsView() {
  const { data: settings, isLoading, isError } = useSettings();
  const [section, setSection] = useState<Section>('tasks');

  return (
    <>
      <PageHeader
        eyebrowNumber="03"
        eyebrowText="Settings"
        title="Settings"
        rightSlot={<span className="mono-label text-mx-grey-mid">AUTO-SAVED ON BLUR</span>}
      />

      <div className="grid grid-cols-[220px_1fr] gap-mx-5">
        <nav aria-label="Settings sections" className="border-r border-mx-grey-800 pr-mx-4">
          <NavGroup label="WORKSPACE">
            {NAV.filter((n) => n.group === 'workspace').map((n) => (
              <NavItem
                key={n.id}
                label={n.label}
                active={section === n.id}
                onClick={() => setSection(n.id)}
              />
            ))}
          </NavGroup>
          <NavGroup label="DATA">
            {NAV.filter((n) => n.group === 'data').map((n) => (
              <NavItem
                key={n.id}
                label={n.label}
                active={section === n.id}
                onClick={() => setSection(n.id)}
              />
            ))}
          </NavGroup>
        </nav>

        <div className="min-w-0">
          {isLoading && <p className="mono-label text-mx-grey-mid">LOADING…</p>}
          {isError && <p className="text-mx-body-sm text-mx-neg">Could not load settings.</p>}
          {settings && <SectionBody section={section} settings={settings} />}
        </div>
      </div>
    </>
  );
}

function NavGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-mx-4">
      <p className="mono-label mb-mx-2 text-mx-grey-mid">{label}</p>
      <ul className="flex flex-col">{children}</ul>
    </div>
  );
}

function NavItem({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'w-full border-l-2 px-mx-3 py-mx-2 text-left text-mx-body-sm transition-colors',
          active
            ? 'border-l-mx-orange bg-mx-orange/10 text-mx-white'
            : 'border-l-transparent text-mx-grey-mid hover:text-mx-white',
        )}
      >
        {label}
      </button>
    </li>
  );
}

function SectionBody({ section, settings }: { section: Section; settings: SettingsState }) {
  switch (section) {
    case 'profile':
      return <ProfileSection />;
    case 'tasks':
      return <TasksSection settings={settings} />;
    case 'sales':
      return <SalesSection settings={settings} />;
    case 'tags':
      return <TagsSection settings={settings} />;
    case 'claude':
      return <ClaudeSection settings={settings} />;
    case 'integrations':
      return <IntegrationsSection />;
    case 'import':
      return <ImportSection />;
    case 'export':
      return <ExportSection />;
  }
}

function ProfileSection() {
  return (
    <SettingsSection title="Profile">
      <p className="text-mx-body-sm text-mx-grey-mid">
        Account details and passkeys are managed by Clerk. Open the Clerk user profile to enrol or
        remove a passkey, update your name, or change the primary email address.
      </p>
      <p className="mt-mx-4 mono-label text-mx-grey-mid">DEFERRED TO M13</p>
      <p className="mt-mx-1 text-mx-body-sm text-mx-grey-mid">
        Embedded Clerk user profile component lands with the M13 polish pass.
      </p>
    </SettingsSection>
  );
}

function TasksSection({ settings }: { settings: SettingsState }) {
  return (
    <SettingsSection title="Tasks">
      <NumberField
        label="WIP LIMIT (DOING COLUMN)"
        keyName="tasks.wipLimitDoing"
        value={settings['tasks.wipLimitDoing']}
        min={1}
        max={10}
      />
      <BooleanField
        label="AUTO-PULL ENABLED"
        keyName="tasks.autoPullEnabled"
        value={settings['tasks.autoPullEnabled']}
        hint="When a Doing slot opens, pull the next highest-priority Today task in."
      />
      <NumberField
        label="WAITING FOLLOW-UP DAYS"
        keyName="tasks.waitingFollowUpDays"
        value={settings['tasks.waitingFollowUpDays']}
        min={0}
        max={60}
      />
      <SelectField
        label="DEFAULT PRIORITY"
        keyName="tasks.defaultPriority"
        value={settings['tasks.defaultPriority']}
        options={['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']}
      />
    </SettingsSection>
  );
}

function SalesSection({ settings }: { settings: SettingsState }) {
  return (
    <SettingsSection title="Sales">
      <NumberField
        label="DAILY COMMIT TARGET"
        keyName="sales.dailyCommitTargetActions"
        value={settings['sales.dailyCommitTargetActions']}
        min={0}
        max={20}
      />
      <NumberField
        label="3PM BANNER HOUR"
        keyName="sales.unclearedQueueBannerHour"
        value={settings['sales.unclearedQueueBannerHour']}
        min={0}
        max={23}
      />
      <NumberField
        label="STALLED TARGET (DAYS)"
        keyName="sales.stalledTargetDays"
        value={settings['sales.stalledTargetDays']}
        min={1}
        max={180}
      />
      <NumberField
        label="STALLED AWAITING (DAYS)"
        keyName="sales.stalledAwaitingDays"
        value={settings['sales.stalledAwaitingDays']}
        min={1}
        max={180}
      />
      <NumberField
        label="STALLED ACTIVE (DAYS)"
        keyName="sales.stalledActiveDays"
        value={settings['sales.stalledActiveDays']}
        min={1}
        max={180}
      />
      <NumberField
        label="DRIFTING STEP (DAYS)"
        keyName="sales.driftingStepDays"
        value={settings['sales.driftingStepDays']}
        min={1}
        max={180}
      />
      <BooleanField
        label="AUTO-APPLY SUBTASK TEMPLATES"
        keyName="sales.subtaskTemplatesAutoApply"
        value={settings['sales.subtaskTemplatesAutoApply']}
      />
      <BooleanField
        label="ZERO-DAY FLAG ENABLED"
        keyName="sales.zeroDayFlagEnabled"
        value={settings['sales.zeroDayFlagEnabled']}
      />
    </SettingsSection>
  );
}

function TagsSection({ settings }: { settings: SettingsState }) {
  return (
    <SettingsSection title="Tags & templates">
      <ListField
        label="NEW LEAD SUBTASKS"
        keyName="subtaskTemplates.newLead"
        value={settings['subtaskTemplates.newLead']}
      />
      <ListField
        label="NEW OPP SUBTASKS"
        keyName="subtaskTemplates.newOpp"
        value={settings['subtaskTemplates.newOpp']}
      />
      <ListField
        label="AT PROPOSAL SUBTASKS"
        keyName="subtaskTemplates.atProposal"
        value={settings['subtaskTemplates.atProposal']}
      />
      <ListField
        label="POST MEETING SUBTASKS"
        keyName="subtaskTemplates.postMeeting"
        value={settings['subtaskTemplates.postMeeting']}
      />
    </SettingsSection>
  );
}

function ClaudeSection({ settings }: { settings: SettingsState }) {
  return (
    <SettingsSection title="Claude">
      <TextField
        label="API KEY"
        keyName="claude.apiKey"
        value={settings['claude.apiKey'] ?? ''}
        hint="Stored encrypted at rest. Field inference and brief composition use this key when set. Leave empty to keep Claude features disabled."
        type="password"
      />
      <TextField
        label="BRIEF TEMPLATE"
        keyName="claude.briefTemplate"
        value={settings['claude.briefTemplate']}
        hint="Reserved for the morning brief composer (phase two). The template is stored but not invoked in phase one."
        multiline
      />
    </SettingsSection>
  );
}

function IntegrationsSection() {
  return (
    <SettingsSection title="Integrations">
      <p className="text-mx-body-sm text-mx-grey-mid">
        Future integrations will surface here. Phase one has none configured.
      </p>
      <ul className="mt-mx-4 flex flex-col gap-mx-2">
        {['Gmail', 'Slack', 'Xero', 'Calendar', 'Linear'].map((name) => (
          <li
            key={name}
            className="flex items-center justify-between border border-mx-grey-800 bg-mx-grey-900 px-mx-3 py-mx-2"
          >
            <span className="text-mx-body-sm text-mx-grey-300">{name}</span>
            <span className="mono-label text-mx-grey-mid">PHASE TWO</span>
          </li>
        ))}
      </ul>
    </SettingsSection>
  );
}

function ImportSection() {
  const importLeads = useImportMondayLeads();
  const importOpps = useImportMondayOpportunities();

  function handleLeads(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) importLeads.mutate(file);
  }
  function handleOpps(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) importOpps.mutate(file);
  }

  return (
    <SettingsSection title="Import">
      <p className="text-mx-body-sm text-mx-grey-mid">
        Upload Monday-shaped CSV or XLSX exports. The handler maps Monday columns to Workspace
        fields per the migration mapping in spec section 18 and de-duplicates on re-import.
      </p>

      <div className="mt-mx-4 flex flex-col gap-mx-3">
        <UploadRow
          label="Monday leads pipeline"
          accept=".xlsx,.csv"
          onChange={handleLeads}
          pending={importLeads.isPending}
          result={importLeads.data}
          error={importLeads.error?.message}
        />
        <UploadRow
          label="Monday opportunities pipeline"
          accept=".xlsx,.csv"
          onChange={handleOpps}
          pending={importOpps.isPending}
          result={importOpps.data}
          error={importOpps.error?.message}
        />
      </div>
    </SettingsSection>
  );
}

function ExportSection() {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);

  async function download(path: string, filename: string) {
    setBusy(path);
    try {
      const token = await getToken();
      if (!token) throw new Error('No active session');
      await downloadExport(path, filename, token);
    } finally {
      setBusy(null);
    }
  }

  return (
    <SettingsSection title="Export">
      <p className="text-mx-body-sm text-mx-grey-mid">
        Download CSV or JSON snapshots of each domain. Use Backup All for the full export envelope.
      </p>
      <div className="mt-mx-4 flex flex-col gap-mx-2">
        {[
          { name: 'Tasks', path: '/api/v1/export/tasks?format=csv', file: 'tasks.csv' },
          { name: 'Leads', path: '/api/v1/export/leads?format=csv', file: 'leads.csv' },
          {
            name: 'Opportunities',
            path: '/api/v1/export/opportunities?format=csv',
            file: 'opportunities.csv',
          },
          { name: 'Backup all (JSON)', path: '/api/v1/export/all', file: 'backup.json' },
        ].map((d) => (
          <div
            key={d.name}
            className="flex items-center justify-between border border-mx-grey-800 bg-mx-grey-900 px-mx-3 py-mx-2"
          >
            <span className="text-mx-body-sm text-mx-grey-300">{d.name}</span>
            <button
              type="button"
              onClick={() => download(d.path, d.file)}
              disabled={busy === d.path}
              className="flex h-8 items-center gap-mx-2 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-mono-sm text-mx-grey-300 hover:border-mx-orange hover:text-mx-orange disabled:text-mx-grey-mid"
            >
              <ArrowDownTrayIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />
              {busy === d.path ? 'Downloading…' : 'Download'}
            </button>
          </div>
        ))}
      </div>
    </SettingsSection>
  );
}

function SettingsSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-mx-4">
      <h2 className="font-display text-mx-h2 font-semibold text-mx-white">{title}</h2>
      {children}
    </section>
  );
}

// ─── Field primitives ──────────────────────────────────────────────

function NumberField<K extends keyof SettingsState>({
  label,
  keyName,
  value,
  min,
  max,
}: {
  label: string;
  keyName: K;
  value: number;
  min: number;
  max: number;
}) {
  const update = useUpdateSettings();
  const [local, setLocal] = useState(String(value));
  return (
    <FieldRow label={label}>
      <input
        type="number"
        min={min}
        max={max}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          const num = parseInt(local, 10);
          if (!Number.isNaN(num) && num !== value)
            update.mutate({ [keyName]: num } as Record<K, number>);
        }}
        className="tnum h-9 w-[120px] border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
      />
    </FieldRow>
  );
}

function BooleanField<K extends keyof SettingsState>({
  label,
  keyName,
  value,
  hint,
}: {
  label: string;
  keyName: K;
  value: boolean;
  hint?: string;
}) {
  const update = useUpdateSettings();
  return (
    <FieldRow label={label} hint={hint}>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => update.mutate({ [keyName]: !value } as Record<K, boolean>)}
        className={cn(
          'flex h-9 items-center gap-mx-2 border px-mx-3 text-mx-mono-sm',
          value
            ? 'border-mx-orange bg-mx-orange/10 text-mx-orange'
            : 'border-mx-grey-800 bg-transparent text-mx-grey-mid',
        )}
      >
        {value && <CheckIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />}
        {value ? 'ON' : 'OFF'}
      </button>
    </FieldRow>
  );
}

function SelectField<K extends keyof SettingsState>({
  label,
  keyName,
  value,
  options,
}: {
  label: string;
  keyName: K;
  value: string;
  options: string[];
}) {
  const update = useUpdateSettings();
  return (
    <FieldRow label={label}>
      <select
        value={value}
        onChange={(e) =>
          update.mutate({ [keyName]: e.target.value } as unknown as Record<K, SettingsState[K]>)
        }
        className="h-9 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </FieldRow>
  );
}

function TextField<K extends keyof SettingsState>({
  label,
  keyName,
  value,
  hint,
  type = 'text',
  multiline = false,
}: {
  label: string;
  keyName: K;
  value: string;
  hint?: string;
  type?: 'text' | 'password';
  multiline?: boolean;
}) {
  const update = useUpdateSettings();
  const [local, setLocal] = useState(value);
  function commit() {
    if (local === value) return;
    update.mutate({ [keyName]: local } as Record<K, string>);
  }
  return (
    <FieldRow label={label} hint={hint}>
      {multiline ? (
        <textarea
          rows={4}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          className="w-full border border-mx-grey-800 bg-transparent px-mx-3 py-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
        />
      ) : (
        <input
          type={type}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          className="w-full border border-mx-grey-800 bg-transparent px-mx-3 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
        />
      )}
      {type === 'password' && (
        <span className="mono-label mt-mx-1 text-mx-grey-mid">ENCRYPTED AT REST</span>
      )}
    </FieldRow>
  );
}

function ListField<K extends keyof SettingsState>({
  label,
  keyName,
  value,
}: {
  label: string;
  keyName: K;
  value: string[];
}) {
  const update = useUpdateSettings();
  const [items, setItems] = useState(value.join('\n'));
  function commit() {
    const next = items
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (JSON.stringify(next) !== JSON.stringify(value))
      update.mutate({ [keyName]: next } as Record<K, string[]>);
  }
  return (
    <FieldRow label={label} hint="One subtask per line.">
      <textarea
        rows={Math.max(3, value.length + 1)}
        value={items}
        onChange={(e) => setItems(e.target.value)}
        onBlur={commit}
        className="w-full border border-mx-grey-800 bg-transparent px-mx-3 py-mx-2 text-mx-body-sm text-mx-grey-300 focus:border-mx-orange focus:outline-none"
      />
    </FieldRow>
  );
}

function FieldRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-mx-1">
      <span className="mono-label text-mx-grey-mid">{label}</span>
      {children}
      {hint && <span className="text-mx-body-sm text-mx-grey-mid">{hint}</span>}
    </div>
  );
}

function UploadRow({
  label,
  accept,
  onChange,
  pending,
  result,
  error,
}: {
  label: string;
  accept: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  pending: boolean;
  result?: { imported: number; skipped: number; errors: { row: number; message: string }[] };
  error?: string;
}) {
  return (
    <div className="border border-mx-grey-800 bg-mx-grey-900 px-mx-3 py-mx-3">
      <div className="flex items-center justify-between gap-mx-3">
        <span className="text-mx-body-sm text-mx-grey-300">{label}</span>
        <label className="flex h-9 cursor-pointer items-center gap-mx-2 border border-mx-grey-800 bg-transparent px-mx-3 text-mx-mono-sm text-mx-grey-300 hover:border-mx-orange hover:text-mx-orange">
          <ArrowUpTrayIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />
          {pending ? 'Uploading…' : 'Choose file'}
          <input
            type="file"
            accept={accept}
            onChange={onChange}
            disabled={pending}
            className="sr-only"
          />
        </label>
      </div>
      {result && (
        <p className="mt-mx-2 mono-label text-mx-pos">
          IMPORTED {result.imported} · SKIPPED {result.skipped}
          {result.errors.length > 0 ? ` · ${result.errors.length} ERRORS` : ''}
        </p>
      )}
      {error && <p className="mt-mx-2 text-mx-body-sm text-mx-neg">{error}</p>}
    </div>
  );
}
